import { useQuery } from "@tanstack/react-query";
import { getCompanyRole } from "@/lib/api/handlers/CompanyRoleHandling";
import { fetchSubscriptions } from "@/lib/api/handlers/SubscriptionHandling";
import { useAuth } from "@/lib/auth/AuthContext";
import { useEventEntitlements } from "@/lib/query/hooks/useEventEntitlements";
import {
  companyTierFromSubscriptions,
  offeredEventPlans,
} from "@/lib/utils/event-plan-catalog";

/**
 * The roles that can do what the sentence offers — upgrade the plan or buy a
 * licence: main-backend BillingResource `subscribe`, `change-plan` and
 * `purchase` are @RolesAllowed OWNER only, and the event's plan page is
 * "managed by {company}" for everyone else (`isCompanyOwner`). An ADMIN could
 * READ the subscriptions (SubscriptionResource), but "you can buy" would be
 * false for them.
 */
const BUYERS: ReadonlySet<string> = new Set(["OWNER"]);

/**
 * Can the company the send was booked against still buy an event licence —
 * from this login?
 *
 * The same rule the purchase screens offer by — `offeredEventPlans` over the
 * company's tier (highest COMPANY-scope row) — and the backend sells by
 * (`assertEventPlanIsAboveCompanyPlan`: the event plan must rank above the
 * company plan). A company on PROFESSIONAL / PROFESSIONAL_YEAR has nothing
 * left to buy. (The backend still sells to a PROFESSIONAL that is cancelling
 * at period end; the purchase screens do not offer it either, so neither does
 * this.)
 *
 * WHICH company (event-scope wave): an event's send debits the company that
 * OWNS the event, and the mails of a licence go to the company that buys it —
 * checkout is pinned to the login company and sells only for its own events.
 * The role and subscriptions reads below answer for the LOGIN company, so they
 * mean something only when that is the owner: `eventId` names the job's event,
 * its owning company comes from the entitlements read, and on another
 * company's event the answer is `false` whatever the login company could buy
 * (the plan is then not even read). The entitlements body carries neither the
 * owning company's tier nor whether this caller could buy for it elsewhere, so
 * there is nothing to say "yes" on there. A company-scope job (`eventId`
 * empty) is the login company's own by construction.
 *
 * WHO: only the login company's OWNER — the one role that can upgrade the
 * plan or buy (`BUYERS`). Everyone else gets `false`, and the plan is not
 * read for them.
 *
 * `null` = unknown: while loading, on a failed read, and for an event whose
 * company is not known (not read yet, read failed, orphan event, no login
 * company). Callers that would PROMISE a purchase treat unknown as "no"
 * (`emailQuotaExceededKey`).
 *
 * Reads only when `enabled`: the job panel asks for it only for a send the
 * consumer refused for the quota. The role read shares the job panel's own
 * cache key (`["userLayout", "companyRole"]`, same query function).
 */
export function useCanBuyEventLicence(
  enabled: boolean,
  eventId?: string | null,
): boolean | null {
  const { state: authState } = useAuth();
  const loginCompanyId = authState.kind === "authenticated" ? authState.companyId : null;

  const eventScoped = !!eventId;
  const entitlementsQ = useEventEntitlements(enabled && eventScoped ? eventId : null);
  const owningCompanyId = entitlementsQ.data?.company?.companyId ?? null;
  // true = the login company's own (event or company-scope job), false =
  // another company's event, null = not known.
  const own: boolean | null = !eventScoped
    ? true
    : !owningCompanyId || !loginCompanyId
      ? null
      : owningCompanyId === loginCompanyId;
  const readPlan = enabled && own === true;

  const roleQ = useQuery({
    queryKey: ["userLayout", "companyRole"],
    queryFn: () => getCompanyRole("role"),
    enabled: readPlan,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const role = roleOf(roleQ.data);
  const canBuy = role !== null && BUYERS.has(role);

  const subsQ = useQuery({
    queryKey: ["eventLicenceOffer", "companySubscriptions"],
    queryFn: () => fetchSubscriptions("scope,type"),
    enabled: readPlan && canBuy,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (!enabled) return null;
  if (own === false) return false;
  if (own === null) return null;
  // A role that is known and is not a buyer's: "no". Not known yet: unknown.
  if (!canBuy) return role === null ? null : false;
  const subs = subsQ.data;
  if (!Array.isArray(subs)) return null; // loading, or a ServerError
  return offeredEventPlans(companyTierFromSubscriptions(subs)).length > 0;
}

function roleOf(data: unknown): string | null {
  if (!data || typeof data !== "object" || "isServerError" in data) return null;
  const r = data as { role?: string; legacyRoleFloor?: string };
  return (r.role === "CUSTOM" ? r.legacyRoleFloor : r.role) ?? null;
}
